/**
 * TRANG GIỚI THIỆU QUÁN.
 *
 * Trang công khai, nằm ngoài Layout như trang Tin tức: đây là link gửi cho
 * khách chưa có tài khoản. Vẫn chạy trong vỏ .app-shell 430px như mọi màn khác
 * của app — mở trên máy tính thì hiện đúng một cột bề ngang điện thoại, không
 * có bố cục desktop riêng.
 *
 * Bố cục dùng chung cho mọi quán: hai quán khác nhau ở dữ liệu và màu thương
 * hiệu, không khác ở bố cục. Vì thế trong cả thư mục này không có tên quán nào
 * được viết cứng, và màu nhấn đi qua biến --venue-accent gán từ
 * venue.branding.primaryColor.
 *
 * Trang không có luồng đặt bàn. Hành động duy nhất là GỌI HOTLINE: nút chính ở
 * hero, thanh dính đáy màn hình, và mọi thẻ giá / thẻ ưu đãi đều là link tel:.
 *
 * Mục nào không có dữ liệu thì ẩn hẳn cùng mục tương ứng trên thanh neo —
 * không dựng empty state, không chừa khoảng trắng.
 *
 * Dữ liệu lấy từ GET /public/venues/:id qua hooks/venue.ts. Backend đã lọc ưu
 * đãi còn hiệu lực và sắp sẵn thứ tự mọi danh sách; trang này không lọc lại.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BilliardLoader from "../../components/BilliardLoader";
import TIcon from "../../components/ui/TIcon";
import AboutSection from "./AboutSection";
import HeroSection from "./HeroSection";
import PricingSection from "./PricingSection";
import PromotionSection from "./PromotionSection";
import { useVenuePage } from "../../hooks/venue";
import { telHref } from "../../helpers/venue";
import "./venue.css";

const NAV_ITEMS = [
  { id: "gioi-thieu", label: "Giới thiệu" },
  { id: "bang-gia", label: "Bảng giá" },
  { id: "uu-dai", label: "Ưu đãi" },
];

const VenuePage: React.FC = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();

  const { data, loading, failed, reload } = useVenuePage(venueId ?? "");
  const [activeSection, setActiveSection] = useState("gioi-thieu");

  /**
   * Mốc thời gian mà từ đó mới cho phép scroll-spy nói lại.
   *
   * Bấm một mục trên thanh neo thì mục đó phải sáng NGAY, và phải sáng kể cả khi
   * cuộn không tới nơi: mục cuối trang, hoặc trang ngắn hơn màn hình, thì section
   * đó không bao giờ lên được vùng quan sát nên scroll-spy một mình không đủ. Đã
   * dựng lại được: mở trang quán trên cửa sổ cao 1250px, bấm "Bảng giá" thì trang
   * chỉ cuộn được tới 94px rồi hết, và mục sáng vẫn là "Giới thiệu".
   *
   * Khoá trong lúc cuộn mượt còn chạy, nếu không thì chính những lần observer bắn
   * ra giữa đường sẽ ghi đè lên lựa chọn của người dùng.
   */
  const khoaSpyToi = useRef(0);

  useEffect(() => {
    document.title = data ? `${data.venue.name} — Biadi` : "Biadi";
  }, [data]);

  const hasAbout = Boolean(
    data &&
      (data.venue.description ||
        data.venue.amenities.length > 0 ||
        data.venue.contacts.fanpage ||
        data.venue.contacts.website ||
        data.venue.contacts.email)
  );
  const hasPricing = Boolean(data && data.pricing.length > 0);
  const hasPromotions = Boolean(data && data.promotions.length > 0);

  const navItems = NAV_ITEMS.filter((item) => {
    if (item.id === "gioi-thieu") return hasAbout;
    if (item.id === "bang-gia") return hasPricing;
    return hasPromotions;
  });
  const navKey = navItems.map((item) => item.id).join(",");

  useEffect(() => {
    if (!data) {
      return;
    }
    const targets = navKey
      .split(",")
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];
    if (targets.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < khoaSpyToi.current) {
          return;
        }
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) {
          setActiveSection(visible.target.id);
        }
      },
      // Vùng quan sát bắt đầu ngay dưới thanh neo dính, để mục được tô sáng
      // đúng là mục người dùng đang đọc chứ không phải mục nằm sau thanh neo.
      { rootMargin: "-64px 0px -55% 0px", threshold: 0 }
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [data, navKey]);

  /**
   * Trang này mở được từ trong app (khối "Quán nổi bật" ở trang chủ) và từ một
   * link chia sẻ ra ngoài. Trường hợp sau không có gì để lùi lại, nên về trang
   * chủ thay vì bấm nút mà không thấy gì xảy ra.
   */
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="venue main app-shell no-tabbar px-5 relative top-0">
        <BilliardLoader variant="center" label="Đang tải thông tin quán…" />
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="venue main app-shell no-tabbar px-5 relative top-0">
        <div className="ui-card empty-state" style={{ marginTop: 120 }}>
          <TIcon name="alert-triangle" />
          <div className="t">Không tải được thông tin quán</div>
          <div className="h">Kiểm tra kết nối rồi thử lại.</div>
          <button
            type="button"
            className="vn-btn vn-btn-primary vn-btn-sm"
            style={{ marginTop: 16 }}
            onClick={reload}
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  const { venue } = data;

  return (
    <div
      className="venue main app-shell no-tabbar px-5 relative top-0"
      style={
        venue.branding.primaryColor
          ? ({
              "--venue-accent": venue.branding.primaryColor,
            } as React.CSSProperties)
          : undefined
      }
    >
      {/* Thanh neo đặt TRƯỚC hero: nó chở nút quay lại, mà nút quay lại không
          thể đợi người dùng cuộn hết hero mới xuất hiện. */}
      <nav className="vn-nav bleed" aria-label="Điều hướng nội dung trang">
        <button
          type="button"
          className="vn-back"
          onClick={goBack}
          aria-label="Quay lại"
        >
          <TIcon name="arrow-left" />
        </button>
        <div className="vn-nav-links">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`vn-nav-link${
                activeSection === item.id ? " active" : ""
              }`}
              aria-current={activeSection === item.id ? "true" : undefined}
              onClick={() => {
                setActiveSection(item.id);
                khoaSpyToi.current = Date.now() + 900;
                document
                  .getElementById(item.id)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <HeroSection venue={venue} />

      {hasAbout ? <AboutSection venue={venue} /> : null}
      {hasPricing ? (
        <PricingSection items={data.pricing} hotline={venue.hotline} />
      ) : null}
      {hasPromotions ? (
        <PromotionSection
          promotions={data.promotions}
          hotline={venue.hotline}
        />
      ) : null}

      {/* Thanh gọi dính đáy: hành động duy nhất của trang, luôn trong tầm ngón
          tay dù khách đang đọc tới đâu. Chỗ này bỏ trống được vì trang không có
          thanh tab dưới. */}
      <div className="vn-callbar-spacer" />
      <a className="vn-callbar" href={telHref(venue.hotline)}>
        <TIcon name="phone" />
        Gọi đặt bàn · {venue.hotline}
      </a>
    </div>
  );
};

export default VenuePage;
